/**
 * SKU列表表格列定义
 */

import React from 'react';
import { Tag, Space, Button, Tooltip, Popconfirm } from 'antd';
import { EyeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { SKUStrategyItem } from '../hooks/useOperationStrategyList';

// 状态配置（与后端 SKU.STATUS_CHOICES 保持一致）
const STATUS_MAP: Record<string, { color: string; text: string }> = {
    'developing': { color: 'processing', text: '开发中' },
    'sample': { color: 'warning', text: '样品测试' },
    'active': { color: 'success', text: '在售' },
    'inactive': { color: 'default', text: '停售' },
    'clearance': { color: 'error', text: '清仓' },
};

interface GetColumnsOptions {
    onView: (record: SKUStrategyItem) => void;
    onDelete: (record: SKUStrategyItem) => void;
    onEdit: (record: SKUStrategyItem) => void;
}

export const getSKUColumns = ({ onView, onDelete, onEdit }: GetColumnsOptions): ColumnsType<SKUStrategyItem> => [
    {
        title: 'SKU名称',
        dataIndex: 'name',
        key: 'name',
        width: 280,
        fixed: 'left',
        render: (text, record) => (
            <div style={{ cursor: 'pointer' }} onClick={() => onView(record)}>
                <div style={{ fontWeight: 500, color: '#1890ff' }}>{text}</div>
                {record.sku_code && (
                    <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                        SKU: {record.sku_code}
                    </div>
                )}
            </div>
        ),
    },
    {
        title: 'ASIN',
        dataIndex: 'asin',
        key: 'asin',
        width: 180,
        render: (text, record) => {
            const asins = new Set<string>();
            if (text) asins.add(text);
            if (record.seller_mappings) {
                record.seller_mappings.forEach(m => {
                    if (m.asin) asins.add(m.asin);
                });
            }
            if (asins.size === 0) return '-';
            return (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {Array.from(asins).map(asin => (
                        <Tag key={asin} color="blue">{asin}</Tag>
                    ))}
                </div>
            );
        },
    },
    {
        title: '售价',
        dataIndex: 'price',
        key: 'price',
        width: 100,
        align: 'right',
        render: (price) => {
            const numPrice = Number(price);
            return price && !isNaN(numPrice) ? `$${numPrice.toFixed(2)}` : '-';
        },
    },
    {
        title: '利润率',
        dataIndex: 'profit_margin',
        key: 'profit_margin',
        width: 100,
        align: 'right',
        render: (margin) => {
            const numMargin = Number(margin);
            if (!margin || isNaN(numMargin)) return '-';
            const color = numMargin >= 30 ? 'success' : numMargin >= 15 ? 'warning' : 'error';
            return <Tag color={color}>{numMargin.toFixed(1)}%</Tag>;
        },
    },
    {
        title: '成本',
        dataIndex: 'cost_price',
        key: 'cost_price',
        width: 100,
        align: 'right',
        render: (cost) => {
            const numCost = Number(cost);
            return cost && !isNaN(numCost) ? `$${numCost.toFixed(2)}` : '-';
        },
    },
    {
        title: '采购成本',
        dataIndex: 'purchase_cost_cny',
        key: 'purchase_cost_cny',
        width: 110,
        align: 'right',
        render: (cost) => {
            const numCost = Number(cost);
            return cost && !isNaN(numCost) ? `¥${numCost.toFixed(2)}` : '-';
        },
    },
    {
        title: '状态',
        dataIndex: 'status',
        key: 'status',
        width: 90,
        render: (status) => {
            const config = STATUS_MAP[status] || { color: 'default', text: status || '未知' };
            return <Tag color={config.color}>{config.text}</Tag>;
        },
    },
    {
        title: '操作',
        key: 'action',
        width: 140,
        fixed: 'right',
        render: (_, record) => (
            <Space size="small">
                <Tooltip title="编辑">
                    <Button
                        type="default"
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => onEdit(record)}
                    />
                </Tooltip>
                <Tooltip title="进入详情">
                    <Button
                        type="primary"
                        icon={<EyeOutlined />}
                        size="small"
                        onClick={() => onView(record)}
                    >
                        详情
                    </Button>
                </Tooltip>
                <Popconfirm
                    title="确定删除此SKU?"
                    description="删除后不可恢复"
                    onConfirm={() => onDelete(record)}
                    okText="确定"
                    cancelText="取消"
                >
                    <Tooltip title="删除">
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                        />
                    </Tooltip>
                </Popconfirm>
            </Space>
        ),
    },
];

export default getSKUColumns;
